/**
 * Collection View — user's card collection with import/export, filters, and totals.
 */
import { useState, useEffect, useMemo } from 'react';
import { fetchJson, putJson, postJson } from './api.js';
import CardTile from './CardTile.jsx';
import CardDetailModal from './CardDetailModal.jsx';
import { SET_OPTIONS, getDisplayPriceValue } from './filterOptions.js';
import { readPriceSettings, formatPriceValue } from './priceUtils.js';

export default function CollectionView() {
  const [entries, setEntries] = useState([]);
  const [cards, setCards] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [importText, setImportText] = useState('');
  const [replaceMode, setReplaceMode] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [search, setSearch] = useState('');
  const [setFilter, setSetFilter] = useState('');
  const [{ showPrices, pricePeriod }] = useState(() => readPriceSettings());

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchJson('/api/collection');
        setEntries(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (!entries.length) {
      setCards({});
      return;
    }
    let cancelled = false;
    const cardIds = [...new Set(entries.map((e) => e.card_id))];
    (async () => {
      const map = {};
      for (const id of cardIds) {
        if (cancelled) return;
        try {
          const params = new URLSearchParams();
          if (showPrices && pricePeriod) {
            params.set('period', pricePeriod);
          }
          const path = params.toString()
            ? `/api/cards/${encodeURIComponent(id)}?${params.toString()}`
            : `/api/cards/${encodeURIComponent(id)}`;
          const c = await fetchJson(path);
          map[c.id] = c;
        } catch {
          map[id] = { id, name: id };
        }
      }
      if (!cancelled) setCards(map);
    })();
    return () => { cancelled = true; };
  }, [entries, showPrices, pricePeriod]);

  const updateQuantity = async (cardId, quantity, foilQuantity) => {
    try {
      await putJson('/api/collection', {
        card_id: cardId,
        quantity: quantity ?? 0,
        foil_quantity: foilQuantity ?? 0
      });
      setEntries((prev) => {
        const next = prev.filter((e) => e.card_id !== cardId);
        if ((quantity ?? 0) + (foilQuantity ?? 0) > 0) {
          next.push({ card_id: cardId, quantity: quantity ?? 0, foil_quantity: foilQuantity ?? 0 });
        }
        return next;
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleImport = async () => {
    const lines = importText.trim().split(/\r?\n/).filter(Boolean);
    const header = lines[0].toLowerCase();
    const start = header.includes('id') || header.includes('card_id') ? 1 : 0;
    const rows = lines.slice(start).map((line) => {
      const parts = line.split(',').map((p) => p.trim().replace(/^"|"$/g, ''));
      return {
        card_id: parts[0] || parts[1],
        quantity: parseInt(parts[1] ?? parts[2] ?? 0, 10) || 0,
        foil_quantity: parseInt(parts[2] ?? parts[3] ?? 0, 10) || 0
      };
    }).filter((r) => r.card_id);
    try {
      await postJson('/api/collection/import', { rows, replace: replaceMode });
      setEntries([]);
      setImportText('');
      const data = await fetchJson('/api/collection');
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExport = () => {
    const header = 'id,normal_count,foil_count\n';
    const csv = header + entries.map((e) => `${e.card_id},${e.quantity || 0},${e.foil_quantity || 0}`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `riftbound-collection-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const collectionWithCards = useMemo(
    () => entries
      .map((e) => ({ ...e, card: cards[e.card_id] }))
      .filter((e) => e.card)
      .sort((a, b) => (a.card?.name || '').localeCompare(b.card?.name || '')),
    [entries, cards]
  );

  const filteredCollection = useMemo(() => {
    const term = search.trim().toLowerCase();
    return collectionWithCards.filter((e) => {
      const name = (e.card.name || '').toLowerCase();
      const id = (e.card.id || '').toLowerCase();
      const setCode = e.card.set?.value?.id || '';
      if (setFilter && setCode !== setFilter) return false;
      if (term && !name.includes(term) && !id.includes(term)) return false;
      return true;
    });
  }, [collectionWithCards, search, setFilter]);

  const totals = useMemo(() => {
    let totalCards = 0;
    let totalEntries = filteredCollection.length;
    let totalValue = 0;

    for (const e of filteredCollection) {
      const normal = Number(e.quantity || 0);
      const foil = Number(e.foil_quantity || 0);
      totalCards += normal + foil;
      if (showPrices && e.card && e.card.price) {
        const rarity = e.card.rarity?.value?.label || e.card.rarity || 'Common';
        const typeLabel = e.card.cardType?.type?.[0]?.label || e.card.cardType?.type?.[0]?.id || '';
        const price = e.card.price;
        const normalPer = getDisplayPriceValue(price, rarity, typeLabel, !!e.card.promo) ?? 0;
        const foilPer = price.foil ?? price.normal ?? 0;
        totalValue += normal * (normalPer || 0) + foil * (foilPer || 0);
      }
    }

    return {
      totalEntries,
      totalCards,
      totalValue,
      totalValueLabel: showPrices ? formatPriceValue(totalValue) : null
    };
  }, [filteredCollection, showPrices]);

  if (error && !entries.length) {
    return (
      <div className="collection-view">
        <h1>Collection</h1>
        <p className="gallery-error">{error}</p>
        <p>Log in or use mock user to view your collection.</p>
      </div>
    );
  }

  return (
    <div className="collection-view">
      {selectedCard && (
        <CardDetailModal card={selectedCard} onClose={() => setSelectedCard(null)} />
      )}
      <div className="gallery-header">
        <h1>Collection</h1>
        <p className="gallery-stats">
          {loading
            ? 'Loading...'
            : `${totals.totalEntries} entries · ${totals.totalCards} cards` }
          {totals.totalValueLabel && (
            <> · Total value {totals.totalValueLabel}</>
          )}
        </p>
      </div>

      <div className="collection-actions">
        <div className="collection-actions-left">
          <div className="collection-view-toggle">
            <button
              type="button"
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              Grid
            </button>
            <button
              type="button"
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
          </div>
          <div className="collection-filters-inline">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="filter-select filter-select-compact"
              value={setFilter}
              onChange={(e) => setSetFilter(e.target.value)}
            >
              <option value="">All sets</option>
              {SET_OPTIONS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="collection-actions-right">
          <button type="button" className="search-button" onClick={handleExport}>
            Export CSV
          </button>
          <div className="import-section">
            <label>
              <input
                type="checkbox"
                checked={replaceMode}
                onChange={(e) => setReplaceMode(e.target.checked)}
              />
              Replace collection
            </label>
            <textarea
              className="import-textarea"
              placeholder="id, normal_count, foil_count"
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              rows={3}
            />
            <button type="button" className="search-button" onClick={handleImport}>
              Import CSV
            </button>
          </div>
        </div>
      </div>

      {loading && !entries.length ? (
        <div className="gallery-loading">Loading collection...</div>
      ) : viewMode === 'list' ? (
        <div className="collection-list">
          <table>
            <thead>
              <tr>
                <th>Card</th>
                <th>Set</th>
                <th>Normal</th>
                <th>Foil</th>
                {showPrices && <th>Value</th>}
              </tr>
            </thead>
            <tbody>
              {filteredCollection.map((e) => {
                const card = e.card;
                let valueLabel = null;
                if (showPrices && card && card.price) {
                  const rarity = card.rarity?.value?.label || card.rarity || 'Common';
                  const typeLabel = card.cardType?.type?.[0]?.label || card.cardType?.type?.[0]?.id || '';
                  const perNormal = getDisplayPriceValue(card.price, rarity, typeLabel, !!card.promo) ?? 0;
                  const perFoil = card.price.foil ?? card.price.normal ?? 0;
                  const total =
                    (e.quantity || 0) * (perNormal || 0) +
                    (e.foil_quantity || 0) * (perFoil || 0);
                  valueLabel = formatPriceValue(total);
                }
                return (
                  <tr key={e.card_id} onClick={() => setSelectedCard(card)} className="collection-row">
                    <td>
                      <span className="collection-card-name">{card.name}</span>
                      <span className="collection-card-id">{card.id}</span>
                    </td>
                    <td>{card.set?.value?.id || ''}</td>
                    <td>{e.quantity || 0}</td>
                    <td>{e.foil_quantity || 0}</td>
                    {showPrices && <td>{valueLabel || '—'}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="cards-grid">
          {filteredCollection.map((e) => (
            <div key={e.card_id} className="collection-tile-wrap">
              <CardTile
                card={e.card}
                quantity={e.quantity}
                foilQuantity={e.foil_quantity}
                onClick={() => setSelectedCard(e.card)}
                showPrice={showPrices}
                pricePeriod={pricePeriod}
              />
              <div className="collection-qty-actions">
                <button
                  type="button"
                  onClick={() => updateQuantity(e.card_id, (e.quantity || 0) + 1, e.foil_quantity || 0)}
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => updateQuantity(e.card_id, e.quantity || 0, (e.foil_quantity || 0) + 1)}
                >
                  ✨+
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && entries.length > 0 && collectionWithCards.length === 0 && (
        <p className="no-results">Resolving card data...</p>
      )}
    </div>
  );
}
